import * as d3 from 'd3'
import Chart from '../../ultils/Chart'
import emptyThumb from '../../assets/images/emptydata.svg'
import './styles.css'
import d3tip from '../../ultils/d3-tip'
import {
  countYAxisTick,
  FUNDAMENTAL_TOGGLE_COLOR,
} from '../../ultils/chart-d3-utils'
import {
  getImpliedRatingRange,
  IMPLIED_RATING_LIST,
} from '../../ultils/dataUtil'

class ImpliedRatingChart extends Chart {
  init({ data, inModal }) {
    let vis = this
    vis.data = data
    vis.inModal = inModal

    vis.parseTime = d3.timeParse('%d/%m/%Y')
    vis.formatTime = d3.timeFormat('%d/%m/%Y')
    vis.bisectDate = d3.bisector(d => d.date).left

    vis.g
      .append('path')
      .attr('class', 'line1')
      .attr('fill', 'none')
      .attr('stroke', 'grey')
      .attr('stroke-width', '3px')

    vis.g
      .append('path')
      .attr('class', 'line2')
      .attr('fill', 'none')
      .attr('stroke', 'grey')
      .attr('stroke-width', '3px')

    vis.x = d3.scaleTime()
    vis.y = d3.scaleOrdinal()

    vis.xAxisCall = d3
      .axisBottom()
      .ticks(inModal ? 8 : 2)
      .tickSizeOuter(0)
    vis.yAxisCall = d3
      .axisLeft()
      .ticks(inModal ? 6 : 4)
      .tickSizeOuter(0)

    vis.xAxis = vis.g
      .append('g')
      .attr('class', 'x axisLineChart')
      .attr('transform', `translate(0, ${vis.HEIGHT})`)
    vis.yAxis = vis.g.append('g').attr('class', 'y axisLineChart')

    vis.focus = vis.g
      .append('g')
      .attr('class', 'focus')
      .style('display', 'none')

    vis.focus
      .append('line')
      .attr('class', 'x-hover-line hover-line')
      .attr('y1', 0)
      .attr('y2', vis.HEIGHT)

    vis.yLegend = vis.svg
      .append('text')
      .attr('class', inModal ? 'axis-label label-in-modal' : 'axis-label')
      .attr('text-anchor', 'end')
      .attr('x', inModal ? 110 : 50)
      .attr('y', 10)
    vis.createToolTip()
    vis.update({ data })
  }

  formatNumber(number) {
    return !isNaN(number) ? Number(number).toFixed(2) : number
  }

  createToolTip() {
    const vis = this
    const inModal = vis.inModal ? '-inModal' : '-notInModal'
    d3.selectAll(`.impliedRating-chart`).remove()
    vis.tip = d3tip()
      .attr('class', `d3-tip-implied-rating-chart impliedRating-chart`)
      .html((e, d) => vis.generateTooltipHtml(d))
    vis.g.call(vis.tip)
  }

  generateTooltipHtml(data) {
    const vis = this
    let returnDiv = `<div class="tip-content">`
    returnDiv += `<div class="row">
                      <div class="line-chart-row-title">
                        <span class="dot" style="background-color: ${FUNDAMENTAL_TOGGLE_COLOR.RISK_EVENTS}"></span> Implied Rating: ${data.implied_rating} 
                      </div>
                    <div class="line-chart-row-content"></div>
                    </div>`
    returnDiv += `</div></div></div>`
    return returnDiv
  }

  mousemove(e) {
    let vis = this
    if (vis.data.length > 0) {
      const x0 = vis.x.invert(d3.pointer(e)[0] - vis.MARGIN.LEFT)
      const i = vis.bisectDate(vis.data, x0, 1, vis.data.length - 1)
      const d0 = vis.data[i - 1]
      const d1 = vis.data[i]
      const d = x0 - d0.date > d1.date - x0 ? d1 : d0

      vis.focus
        .select('.x-hover-line')
        .attr('x1', vis.x(d.date))
        .attr('x2', vis.x(d.date))

      vis.focus.selectAll('circle').remove()
      if (d.hasOwnProperty('implied_rating')) {
        vis.focus
          .append('circle')
          .attr('class', 'impliedRatingDot')
          .attr('r', 4)
          .attr('fill', FUNDAMENTAL_TOGGLE_COLOR.RISK_EVENTS)

        vis.focus
          .select('.impliedRatingDot')
          .attr('cx', vis.x(d.date))
          .attr('cy', vis.y(d['implied_rating']))
      }
      vis.tip.show(e, d, 10, -d3.pointer(e)[1] - 40)
    }
  }

  update({ data }) {
    let vis = this
    vis.data = data || vis.data
    vis.t = d3.transition().duration(1000)
    vis.svg.selectAll('image').remove()

    if (vis.data.length <= 1) {
      // treat 1 record as empty state to clear xAxis & yAxis value
      vis.data = []
      vis.svg
        .append('svg:image')
        .attr('xlink:href', emptyThumb)
        .attr('x', vis.WIDTH / 2 - 77 + vis.MARGIN.LEFT)
        .attr('y', vis.HEIGHT / 2 - 37 + vis.MARGIN.TOP)
        .attr('width', '155')
        .attr('height', '73')
      vis.yLegend.text('')
    } else {
      vis.yLegend.text(vis.inModal ? 'Standard Deviation' : '')
    }

    vis.x.range([0, vis.WIDTH]).domain(d3.extent(vis.data, d => d.date))
    vis.y.range(getImpliedRatingRange(vis.HEIGHT)).domain(IMPLIED_RATING_LIST)

    vis.xAxisCall = d3
      .axisBottom()
      .ticks(countYAxisTick(vis.data))
      .tickSizeOuter(0)

    vis.xAxisCall.scale(vis.x)
    vis.xAxis.transition(vis.t).call(vis.xAxisCall)
    vis.yAxisCall.scale(vis.y)
    vis.yAxis.transition(vis.t).call(vis.yAxisCall)

    vis.impliedRatingLine = d3
      .line()
      .curve(d3.curveLinear)
      .x(d => vis.x(d.date))
      .y(d => vis.y(d.implied_rating))

    vis.g
      .select('.line1')
      .attr('stroke-width', '1.5px')
      .attr('stroke', FUNDAMENTAL_TOGGLE_COLOR.RISK_EVENTS)
      .transition(vis.t)
      .attr('d', vis.impliedRatingLine(vis.data))

    vis.svg
      .on('mousemove', e => vis.mousemove(e))
      .on('mouseover', () => {
        // vis.tip.hide()
        vis.focus.style('display', null)
      })
      .on('mouseout', () => {
        vis.tip.hide()
        vis.focus.style('display', 'none')
      })
  }
}

export default ImpliedRatingChart
